"use client";

import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "./confirm-dialog";

interface SchemaSaveBarProps {
  dirtyCount: number;
  saving: boolean;
  /** Descendants of the selected category; empty hides the scope control. */
  subcategoryCount: number;
  applyToSubtree: boolean;
  confirmOpen: boolean;
  progress: { done: number; total: number } | null;
  onApplyToSubtreeChange: (value: boolean) => void;
  onConfirmOpenChange: (open: boolean) => void;
  onDiscard: () => void;
  onSave: () => void;
  onConfirmSubtree: () => void;
}

/**
 * Pinned to the panel and shown only when there is something to save, so saving
 * never means scrolling past the whole form.
 */
export function SchemaSaveBar({
  dirtyCount,
  saving,
  subcategoryCount,
  applyToSubtree,
  confirmOpen,
  progress,
  onApplyToSubtreeChange,
  onConfirmOpenChange,
  onDiscard,
  onSave,
  onConfirmSubtree,
}: SchemaSaveBarProps) {
  return (
    <>
      {dirtyCount > 0 && (
        <div className="absolute inset-x-0 bottom-0 flex flex-wrap items-center gap-3 border-t bg-card px-5 py-3 shadow-[0_-8px_20px_rgba(9,9,11,0.06)]">
          <span className="text-[13px] font-medium">
            {dirtyCount === 1 ? "1 unsaved change" : `${dirtyCount} unsaved changes`}
          </span>

          {subcategoryCount > 0 ? (
            <label className="flex cursor-pointer items-center gap-2 text-[12.5px] text-muted-foreground">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 accent-pink-600"
                checked={applyToSubtree}
                disabled={saving}
                onChange={(e) => onApplyToSubtreeChange(e.target.checked)}
              />
              Also apply to all{" "}
              <strong className="text-foreground">{subcategoryCount}</strong>{" "}
              subcategories
            </label>
          ) : (
            <span className="text-xs text-muted-foreground">
              Saving may queue affected products for updating.
            </span>
          )}

          <div className="ml-auto flex items-center gap-2">
            {progress && (
              <span className="text-xs tabular-nums text-muted-foreground">
                Applying… {progress.done} of {progress.total}
              </span>
            )}
            <Button variant="outline" onClick={onDiscard} disabled={saving}>
              Discard
            </Button>
            <Button onClick={onSave} disabled={saving}>
              {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title={`Apply to all ${subcategoryCount} subcategories?`}
        body={
          <>
            All <strong>{subcategoryCount} subcategories</strong> will be changed to
            match this one. Any settings they have of their own will be replaced, and
            this cannot be undone.
            {subcategoryCount > 50 && (
              <> This will take a moment — they are updated one at a time.</>
            )}
          </>
        }
        confirmLabel={`Change all ${subcategoryCount}`}
        destructive
        busy={saving}
        onConfirm={onConfirmSubtree}
        onOpenChange={onConfirmOpenChange}
      />
    </>
  );
}
